import React, { useMemo } from 'react';
import cx from 'classnames';
import {
  EmptyBody,
  EmptyStateLayoutType,
  EmptyStateProps,
  EmptyTitle,
} from './EmptyState';
import ManualUploadButton from './ManualUploadButton';
import EmptyState from './EmptyState';
import FilmIcon from './FilmIcon';
import { assetUrl } from '~/helpers';
import EnterWrittenTextButton from './EnterWrittenTextButton';
import {
  FilteredTagsProps,
  getTagNames,
} from '../../Tags/Components/TagFilter/hooks/useFilteredTags';
import DragAndDropUploadBanner from './DragAndDropUpload/DragAndDropUploadBanner';
import { MyLibraryTab, useLibraryTabs } from '../hooks/use-library-tabs';

export type EmptyUploadVideoProps = Pick<
  EmptyStateProps,
  'layout' | 'className'
> &
  FilteredTagsProps & {
    showDragAndDropBanner?: boolean;
  };

export default function EmptyUploadVideo({
  layout = EmptyStateLayoutType.full,
  className,
  tags = [],
  clearTags,
  showDragAndDropBanner = false,
}: EmptyUploadVideoProps) {
  const handleTagsClearClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    clearTags?.();
  };

  const { activeTabName, redirectToUploadedTab } = useLibraryTabs();

  const onCreateTextAssetSuccess = () => {
    if (activeTabName !== MyLibraryTab.Uploaded) {
      redirectToUploadedTab();
    }
  };

  const { title, body } = useMemo(() => {
    const title = (
      <EmptyTitle
        noun='uploaded asset'
        tagNames={tags.length > 0 ? getTagNames(tags) : undefined}
      />
    );

    const body = (
      <EmptyBody
        noun='uploaded asset'
        layout={layout}
        variant={tags.length > 0 ? 'tagged' : 'default'}
        defaultBody='Upload your own videos/text asset here and share with Bonjoro'
        onYourLibraryClick={handleTagsClearClick}
      />
    );

    return {
      title,
      body,
    };
  }, [tags, layout]);

  const blockLayout = layout === EmptyStateLayoutType.block;

  const buttons = (
    <div
      className={cx('flex gap-2', {
        'flex-col': blockLayout,
      })}
    >
      <ManualUploadButton
        className={cx({
          'w-full justify-center': blockLayout,
        })}
        text='Upload Video'
        icon={<FilmIcon />}
      />
      <EnterWrittenTextButton
        className={cx({
          'w-full': blockLayout,
        })}
        onSuccess={onCreateTextAssetSuccess}
      />
    </div>
  );

  return (
    <>
      <EmptyState
        layout={layout}
        className={className}
        imageUrl={assetUrl('assets/images/my-library/upload.png')}
        title={title}
        body={body}
        actions={buttons}
      />
      {showDragAndDropBanner && (
        <DragAndDropUploadBanner className='mt-6 mx-[5%]' />
      )}
    </>
  );
}
